import React, { useEffect, useRef, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Label,
} from "recharts";
import { Box, Grid, Button, Typography, Snackbar, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { getReturnsByLocation } from "../../../Services/authService";
import SectionLoader from "../../../Components/Common/SectionLoader";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../../../Styles/daterange-responsive.css";
import { DateRange } from "react-date-range";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import ExportIcon from "../../../assets/Export_icon.svg";

const ReturnsByLocation = () => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const calendarRef = useRef(null);
  const calendarButtonRef = useRef(null);

  const textColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;

  const borderColor =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale20;

  const dropIcon =
    mode === "dark" ? DARKCOLORS.GreyScale80 : LIGHTCOLORS.GreyScale80;
  const btnColor1 =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const btnColor =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;

  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;

  const reset =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const colorSuccessExport =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;

  const settingIconColor =
    mode === "dark" ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;

  // Bowls = purple, Cups (bottles) = green — matches the reference design
  const bowlsColor = "#574AE2";
  const cupsColor = "#6BE675";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appliedFromDate, setAppliedFromDate] = useState(null);
  const [appliedToDate, setAppliedToDate] = useState(null);

  const [open1, setOpen1] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [dateRange, setDateRange] = useState([{ key: "selection" }]);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const handleDateChange = (item) => {
    setDateRange([item.selection]);
  };

  const handleCalendarToggle = () => {
    if (calendarButtonRef.current) {
      const rect = calendarButtonRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX,
      });
    }
    setIsCalendarOpen((prev) => !prev);
  };

  const formatFromParam = (date) =>
    dayjs(date).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
  const formatToParam = (date) =>
    dayjs(date).endOf("day").format("YYYY-MM-DDTHH:mm:ss");

  const fetchReturnsData = async (from, to) => {
    try {
    setLoading(true);
    setError(null);

  const params = {
  fromDate: from || "",
  toDate: to || "",
};



    const response = await getReturnsByLocation(params);

    


      // Handle response: could be array directly or { data: [...], fromDate, toDate }
      let dataArray;
      let dataFromDate, dataToDate;

      if (Array.isArray(response)) {
        dataArray = response;
      } else if (response && Array.isArray(response.data)) {
        dataArray = response.data;
        dataFromDate = response.fromDate;
        dataToDate = response.toDate;
      } else {
        dataArray = response || [];
      }

      setData(dataArray);

      // On initial load (no filter), update calendar to reflect actual data range
      if (!from && !to && dataFromDate && dataToDate) {
        setDateRange([
          {
            startDate: new Date(dataFromDate),
            endDate: new Date(dataToDate),
            key: "selection",
          },
        ]);
        setAppliedFromDate(dataFromDate);
        setAppliedToDate(dataToDate);
      }
    } catch (error) {
      console.error(error);
      setError(t("returns_by_location.failed_load"));
    } finally {
      setLoading(false);
    }
  };

  // Default: show all data; calendar button still shows the pre-selected range text
  useEffect(() => {
    fetchReturnsData();
  }, []);

  const resetFilter = () => {
    setDateRange([{ key: "selection" }]);
    setAppliedFromDate(null);
    setAppliedToDate(null);
    setIsCalendarOpen(false);
    fetchReturnsData();
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen1(false);
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      setToastMessage(t("sales_analytics.noDataToExport"));
      setOpen1(true);
      return;
    }

    try {
      const rows = data.map((item) => ({
        [t("returns_by_location.machine_number", "Machine Number")]:
          item.machineNumber || "",
        [t("returns_by_location.location", "Location")]: item.location || "",
        [t("returns_by_location.bowls", "Bowls")]: item.bowls ?? 0,
        [t("returns_by_location.bottles", "Bottles")]: item.bottles ?? 0,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Returns By Location");

      const buf = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;

      const ts = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 14);

      const fileName = t("returns_by_location.export_file_name", "returns_by_location");

a.download = `${fileName}_${ts}.xlsx`;

      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting returns by location:", error);

      setToastMessage(
        t("returns_by_location.export_failed", "Error exporting data"),
      );
      setOpen1(true);
    }
  };

  const handleApply = () => {
  const from = formatFromParam(dateRange[0].startDate);
  const to = formatToParam(dateRange[0].endDate);

  // console.log("SELECTED DATE RANGE:");
  // console.log("startDate:", dateRange[0].startDate);
  // console.log("endDate:", dateRange[0].endDate);
  // console.log("FROM:", from);
  // console.log("TO:", to);

  setAppliedFromDate(from);
  setAppliedToDate(to);
  setIsCalendarOpen(false);

  fetchReturnsData(from, to);
};

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    }

    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);

  const CustomXAxisTick = ({ x, y, payload }) => {
    const item = data[payload.index];

    return (
      <g transform={`translate(${x},${y + 15})`}>
        <text x={0} y={0} textAnchor="middle" fill={textColor} fontSize={11}>
          <tspan x={0} dy="1.2em">
            {item?.location}
          </tspan>
          <tspan x={0} dy="1.2em">
            {item?.machineNumber}
          </tspan>
        </text>
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: btnColor,
            padding: "10px",
            borderRadius: "8px",
            border: `1px solid ${borderColor}`,
          }}
        >
          <Typography>
            {t("returns_by_location.bowls")}: {payload[0]?.value}
          </Typography>
          <Typography>
            {t("returns_by_location.bottles")}: {payload[1]?.value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, position: "relative", gap: 2 }}
      >
        <h3 className="subheading-sec-card" style={{ margin: 0 }}>
          {t("returns_by_location.title")}
        </h3>

        <Box
          className="return-mid-section"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            flex: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: bowlsColor,
              }}
            />
            <Typography sx={{ fontSize: 13, color: textColor }}>
              {t("returns_by_location.naf_bowls")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: cupsColor,
              }}
            />
            <Typography sx={{ fontSize: 13, color: textColor }}>
              {t("returns_by_location.bottles")}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginLeft: "auto",
          }}
        >
          <Button
            className="return-calender"
            ref={calendarButtonRef}
            aria-label="select-date"
            onClick={handleCalendarToggle}
            sx={{
              background: btnColor,
              borderRadius: "50px",
              padding: "8px 14px",
              color: dropIcon,
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: textColor,
                fontWeight: 500,
              }}
            >
              {appliedFromDate && appliedToDate
                ? `${dayjs(appliedFromDate).format("DD MMM YYYY")} - ${dayjs(appliedToDate).format("DD MMM YYYY")}`
                : t("sales_analytics.select_date_range", "Select Date Range")}
            </Typography>

            <CalendarTodayOutlinedIcon style={{ fontSize: "1rem" }} />
          </Button>

          <Box
            onClick={handleExport}
            sx={{
              background: colorSuccessExport,
              color: settingIconColor,
              padding: "6px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "24px",
              height: "24px",
              flexShrink: 0,
              cursor: "pointer",
            }}
          >
            <img
              src={ExportIcon}
              alt="Export"
              style={{
                width: "12px",
                height: "12px",
              }}
            />
          </Box>
        </Box>

        {isCalendarOpen && (
          <div
            className="sales-calender"
            ref={calendarRef}
            style={{
              background: btnColor,
              position: "absolute",
              top: "50px",
              right: "0px",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              borderRadius: "12px",
              border: `1px solid ${borderColor}`,
            }}
          >
            <DateRange
              editableDateInputs={true}
              onChange={handleDateChange}
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem",
                margin: "1rem 0.5rem",
              }}
            >
              {(appliedFromDate || appliedToDate) && (
                <button
                  type="button"
                  style={{
                    background: "transparent",
                    color: reset,
                    borderColor: reset,
                    padding: "10px 15px",
                    fontSize: "13px",
                    borderRadius: "24px",
                    cursor: "pointer",
                  }}
                  onClick={resetFilter}
                >
                  {t("sales_analytics.reset", "Reset")}
                </button>
              )}
              <button
                type="button"
                style={{
                  background: btnColor1,
                  padding: "10px 15px",
                  fontSize: "15px",
                }}
                className="save-btn"
                onClick={handleApply}
              >
                {t("sales_analytics.apply")}
              </button>
            </div>
          </div>
        )}
      </Grid>

      <Box
        sx={{
          width: "100%",
          maxWidth: "100% !important",
          overflowX: "auto !important",
          overflowY: "hidden !important",
          "&::-webkit-scrollbar": {
            width: "10px !important",
            height: "8px !important",
            padding: "2px !important",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: scroll1,
            borderRadius: "10px",
            border: `1px solid ${scroll}`,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scroll,
            borderRadius: "10px",
            width: "3px !important",

            border: "3px solid transparent",
            backgroundClip: "padding-box",
            "&:hover": {
              backgroundColor: scroll,
            },
          },
        }}
      >
        <Box
          sx={{
            minWidth: `${Math.max(data.length * 100, 600)}px`,
          }}
        >
          {loading ? (
            <SectionLoader />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={data}
                barGap={4}
                barCategoryGap="30%"
                margin={{ top: 30, right: 20, left: 0, bottom: 70 }}
              >
                <CartesianGrid
                  strokeDasharray="0"
                  horizontal
                  vertical={false}
                  stroke={borderColor}
                />

                <XAxis
                  dataKey="location"
                  interval={0}
                  height={70}
                  tick={<CustomXAxisTick />}
                >
                  <Label
                    value={t("sales_analytics.location")}
                    position="insideBottom"
                    offset={-55}
                    fill={textColor}
                    fontSize={12}
                  />
                </XAxis>

                <YAxis
                  tick={{ fill: textColor }}
                  tickLine={false}
                  axisLine={false}
                >
                  <Label
                    value={t("sales_analytics.count")}
                    position="insideLeft"
                    offset={10}
                    angle={-90}
                    fill={textColor}
                    fontSize={13}
                  />
                </YAxis>

                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "transparent" }}
                />

                <Bar
                  dataKey="bowls"
                  fill={bowlsColor}
                  barSize={16}
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList dataKey="bowls" position="top" fill={textColor} />
                </Bar>

                <Bar
                  dataKey="bottles"
                  fill={cupsColor}
                  barSize={16}
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    dataKey="bottles"
                    position="top"
                    fill={textColor}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
      <Snackbar
        open={open1}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReturnsByLocation;